'use client';

import { useEffect, useRef, useState, type ReactNode, type TransitionEvent } from 'react';
import { cn } from '~/lib/utils';

export const FEEDBACK_EXIT_COLLAPSE_MS = 400;
export const FEEDBACK_EXIT_FAST_COLLAPSE_MS = 180;
export const FEEDBACK_EXIT_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

type FeedbackListExitRowProps = {
  rowId: string;
  isExiting: boolean;
  isCompactView?: boolean;
  isLastRow?: boolean;
  fastExit?: boolean;
  onExitComplete: (rowId: string) => void;
  children: ReactNode;
};

export default function FeedbackListExitRow({
  rowId,
  isExiting,
  isCompactView,
  isLastRow,
  fastExit,
  onExitComplete,
  children,
}: FeedbackListExitRowProps) {
  const completedRef = useRef(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const onExitCompleteRef = useRef(onExitComplete);
  onExitCompleteRef.current = onExitComplete;

  const collapseMs = fastExit ? FEEDBACK_EXIT_FAST_COLLAPSE_MS : FEEDBACK_EXIT_COLLAPSE_MS;

  const finishExit = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onExitCompleteRef.current(rowId);
  };
  const finishExitRef = useRef(finishExit);
  finishExitRef.current = finishExit;

  useEffect(() => {
    if (!isExiting) {
      completedRef.current = false;
      setIsCollapsing(false);
      return;
    }

    // A re-run (Strict Mode, fastExit/collapseMs change) must not fire again
    // after this row has already committed.
    if (completedRef.current) return;

    let frame1 = 0;
    let frame2 = 0;
    let commitTimer = 0;

    if (prefersReducedMotion()) {
      finishExitRef.current();
    } else {
      frame1 = window.requestAnimationFrame(() => {
        frame2 = window.requestAnimationFrame(() => {
          setIsCollapsing(true);
        });
      });

      commitTimer = window.setTimeout(() => {
        finishExitRef.current();
      }, collapseMs + 50);
    }

    // Always clean up timers. Do not call onExitComplete here — this cleanup
    // also runs when the exit is cancelled (restoreExit) and when the effect
    // re-runs. Completing from cleanup would commit a restored row or fire twice.
    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
      window.clearTimeout(commitTimer);
    };
  }, [collapseMs, isExiting, rowId]);

  const handleTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (!isCollapsing || event.propertyName !== 'grid-template-rows') {
      return;
    }
    finishExit();
  };

  const transitionStyle = {
    transitionDuration: `${collapseMs}ms`,
    transitionTimingFunction: FEEDBACK_EXIT_EASING,
  };

  return (
    <div
      className={cn(
        'grid motion-reduce:transition-none',
        isCollapsing ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
      )}
      style={{
        ...transitionStyle,
        transitionProperty: 'grid-template-rows',
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="min-h-0 overflow-hidden">
        <div
          className={cn(
            !isLastRow && (isCompactView ? 'mb-2' : 'mb-2.5'),
            'motion-reduce:transition-none',
            isCollapsing && 'opacity-0 -translate-y-1',
          )}
          style={{
            ...transitionStyle,
            transitionProperty: 'opacity, transform',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
